import { createSlice } from '@reduxjs/toolkit';

const LIST_KEYS = ['sites', 'directions', 'siteDirections'];

const initialState = {
  included:   { sites: [], directions: [], siteDirections: [] },
  excluded:   { sites: [], directions: [], siteDirections: [] },
  presets: {}
};

/* ---------------- helpers ---------------- */
const ensureArray  = (val) => Array.isArray(val) ? val : (val == null ? [] : [val]);
const ensureUnique = (arr = []) => Array.from(new Set((arr || []).filter(Boolean)));
const isValidKey    = (key) => LIST_KEYS.includes(key);

/** Overwrite one side (included|excluded) for a given key, and keep lists disjoint. */
const overwriteSide = (state, side, key, items) => {
  if (!isValidKey(key)) return;
  const other = side === 'included' ? 'excluded' : 'included';
  const list = ensureUnique(ensureArray(items));

  // ensure arrays exist (helps with old persisted states)
  state[side][key]  = state[side][key]  || [];
  state[other][key] = state[other][key] || [];

  state[side][key]  = list;
  state[other][key] = ensureUnique(state[other][key].filter((x) => !list.includes(x)));
};

/** Move items from one side to the other (add to 'toSide', remove from 'fromSide'). */
const moveItems = (state, toSide, key, items) => {
  if (!isValidKey(key)) return;
  const fromSide = toSide === 'included' ? 'excluded' : 'included';
  const list = ensureUnique(ensureArray(items));
  if (list.length === 0) return;

  state[toSide][key]  = ensureUnique([...(state[toSide][key]  || []), ...list]);
  state[fromSide][key]= ensureUnique((state[fromSide][key] || []).filter((x) => !list.includes(x)));
};

/** Toggle a single item: if excluded -> include; else -> exclude. */
const toggleItemKey = (state, key, item) => {
  if (!isValidKey(key) || !item) return;

  state.included[key] = state.included[key] || [];
  state.excluded[key] = state.excluded[key] || [];

  const idx = state.excluded[key].indexOf(item);
  if (idx >= 0) {
    // was excluded -> include it
    state.excluded[key].splice(idx, 1);
    state.included[key] = ensureUnique([...(state.included[key] || []), item]);
  } else {
    // add to excluded, remove from included
    state.excluded[key] = ensureUnique([...(state.excluded[key] || []), item]);
    state.included[key] = ensureUnique((state.included[key] || []).filter((x) => x !== item));
  }
};

/* ---------------- slice ---------------- */
const mapFiltersSlice = createSlice({
  name: 'mapFiltersSlice',
  initialState,
  reducers: {
    /* Generic setters */
    setExcluded(state, action) {
      const { key, items } = action.payload || {};
      overwriteSide(state, 'excluded', key, items);
    },
    setIncluded(state, action) {
      const { key, items } = action.payload || {};
      overwriteSide(state, 'included', key, items);
    },
    includeItems(state, action) {
      const { key, items } = action.payload || {};
      moveItems(state, 'included', key, items);
    },
    excludeItems(state, action) {
      const { key, items } = action.payload || {};
      moveItems(state, 'excluded', key, items);
    },
    toggleItem(state, action) {
      const { key, item } = action.payload || {};
      toggleItemKey(state, key, item);
    },

    /* ---- Sites ---- */
    setExcludedSites(state, action) {
      overwriteSide(state, 'excluded', 'sites', action.payload);
    },
    setIncludedSites(state, action) {
      overwriteSide(state, 'included', 'sites', action.payload);
    },
    includeSelectedSites(state, action) {
      moveItems(state, 'included', 'sites', action.payload);
    },
    excludeSelectedSites(state, action) {
      moveItems(state, 'excluded', 'sites', action.payload);
    },
    toggleExcludedSite(state, action) {
      toggleItemKey(state, 'sites', action.payload);
    },

    /* ---- Directions ---- */
    setExcludedDirections(state, action) {
      overwriteSide(state, 'excluded', 'directions', action.payload);
    },
    setIncludedDirections(state, action) {
      overwriteSide(state, 'included', 'directions', action.payload);
    },
    includeSelectedDirections(state, action) {
      moveItems(state, 'included', 'directions', action.payload);
    },
    excludeSelectedDirections(state, action) {
      moveItems(state, 'excluded', 'directions', action.payload);
    },
    // kept for back-compat
    excludeDirections(state, action) {
      moveItems(state, 'excluded', 'directions', action.payload);
    },

    /* ---- Site-Directions (siteDirectionName) ---- */
    setExcludedSiteDirections(state, action) {
      overwriteSide(state, 'excluded', 'siteDirections', action.payload);
    },
    setIncludedSiteDirections(state, action) {
      overwriteSide(state, 'included', 'siteDirections', action.payload);
    },
    includeSelectedSiteDirections(state, action) {
      moveItems(state, 'included', 'siteDirections', action.payload);
    },
    excludeSelectedSiteDirections(state, action) {
      moveItems(state, 'excluded', 'siteDirections', action.payload);
    },
    toggleExcludedSiteDirection(state, action) {
      toggleItemKey(state, 'siteDirections', action.payload);
    },

    /* Reset */
    resetFilters(state) {
      state.included = { sites: [], directions: [], siteDirections: [] };
      state.excluded = { sites: [], directions: [], siteDirections: [] };
    }
  }
});

export const {
  // Generic
  setExcluded,
  setIncluded,
  includeItems,
  excludeItems,
  toggleItem,

  // Sites
  setExcludedSites,
  setIncludedSites,
  includeSelectedSites,
  excludeSelectedSites,
  toggleExcludedSite,

  // Directions
  setExcludedDirections,
  setIncludedDirections,
  includeSelectedDirections,
  excludeSelectedDirections,
  excludeDirections,

  // Site-Directions (siteDirectionName)
  setExcludedSiteDirections,
  setIncludedSiteDirections,
  includeSelectedSiteDirections,
  excludeSelectedSiteDirections,
  toggleExcludedSiteDirection,

  // Reset
  resetFilters
} = mapFiltersSlice.actions;

export default mapFiltersSlice.reducer;
